<template>
    <div class="rounded-xl border border-gray-200 bg-white p-6 shadow-sm w-full">
        <!-- Header -->
        <div>
            <div
                class="inline-flex items-center px-3 py-1 rounded-full bg-indigo-100 text-indigo-800 text-xs font-semibold">
                🔒 Change Password</div>
            <h3 class="mt-4 text-2xl font-extrabold text-indigo-900">Update your account password</h3>
            <p class="mt-2 text-gray-600">Use a strong password with at least 6 characters, including numbers and
                symbols.</p>
        </div>

        <!-- Alert -->
        <transition name="fade">
            <div v-if="showAlert" class="mt-4 rounded-lg border px-4 py-3 flex items-center gap-3 shadow-sm"
                :class="{ 'bg-green-50 border-green-200': alertType === 'success', 'bg-red-50 border-red-200': alertType === 'error' }">
                <div class="flex h-8 w-8 items-center justify-center rounded-md"
                    :class="{ 'bg-green-100': alertType === 'success', 'bg-red-100': alertType === 'error' }">
                    <span v-if="alertType === 'success'">✅</span>
                    <span v-else>⚠️</span>
                </div>
                <p class="text-sm"
                    :class="{ 'text-green-900': alertType === 'success', 'text-red-900': alertType === 'error' }">{{
                        alertMessage }}</p>
                <button type="button" class="ml-auto text-sm" @click="dismissAlert">✖</button>
            </div>
        </transition>

        <!-- Form -->
        <form class="mt-6 space-y-4" @submit.prevent="saveBtnclicked">
            <div>
                <label class="block text-sm font-medium text-gray-700">Current Password *</label>
                <input v-model.trim="currentPassword.val" type="password"
                    class="mt-1 w-full rounded-xl border border-gray-300 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 px-4 py-2"
                    @blur="clearValidity('currentPassword')">
                <transition name="fade">
                    <p v-if="!currentPassword.isValid" class="mt-1 text-xs text-red-600">{{ currentPassword.msg }}</p>
                </transition>
            </div>
            <div class="grid sm:grid-cols-2 gap-4">
                <div>
                    <label class="block text-sm font-medium text-gray-700">New Password *</label>
                    <input v-model.trim="password.val" type="password"
                        class="mt-1 w-full rounded-xl border border-gray-300 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 px-4 py-2"
                        @blur="clearValidity('password')">
                    <transition name="fade">
                        <p v-if="!password.isValid" class="mt-1 text-xs text-red-600">{{ password.msg }}</p>
                    </transition>
                </div>
                <div>
                    <label class="block text-sm font-medium text-gray-700">Confirm New Password *</label>
                    <input v-model.trim="confirmPassword.val" type="password"
                        class="mt-1 w-full rounded-xl border border-gray-300 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 px-4 py-2"
                        @blur="clearValidity('confirmPassword')">
                    <transition name="fade">
                        <p v-if="!confirmPassword.isValid" class="mt-1 text-xs text-red-600">{{ confirmPassword.msg }}
                        </p>
                    </transition>
                </div>
            </div>

            <!-- Strength indicator -->
            <div v-if="password.val" class="sm:col-span-2">
                <div class="flex items-center justify-between mb-2">
                    <span class="text-sm font-medium text-gray-700">Password Strength</span>
                    <span class="text-xs" :class="getStrengthTextClass()">{{ getPasswordStrengthLabel() }} ({{
                        getPasswordStrength() }}%)</span>
                </div>
                <div class="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div class="h-2 rounded-full transition-all" :class="getPasswordStrengthClass()"
                        :style="{ width: getPasswordStrength() + '%' }"></div>
                </div>
            </div>

            <div class="pt-2 flex items-center gap-3">
                <button type="submit"
                    class="px-5 py-2 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-sm transition disabled:opacity-60 disabled:cursor-not-allowed"
                    :disabled="isUpdating">
                    <span v-if="isUpdating">Saving...</span>
                    <span v-else>Save</span>
                </button>
            </div>
        </form>
    </div>
</template>

<script>
import gql from 'graphql-tag';
export default {
    name: "ChangePassword",
    data() {
        return {
            isUpdating: false,
            showAlert: false,
            alertMessage: '',
            alertType: 'success',
            currentPassword: { val: '', isValid: true, msg: '' },
            password: { val: '', isValid: true, msg: '' },
            confirmPassword: { val: '', isValid: true, msg: '' },
            formIsValid: true,
        };
    },
    methods: {
        clearValidity(input) { this[input].isValid = true; this[input].msg = ''; },
        validateForm() {
            this.formIsValid = true;
            if (this.currentPassword.val === '') { this.currentPassword.isValid = false; this.currentPassword.msg = 'Current Password must not be empty.'; this.formIsValid = false; }
            if (this.password.val === '') { this.password.isValid = false; this.password.msg = 'New Password must not be empty.'; this.formIsValid = false; }
            else if (this.password.val.length < 6) { this.password.isValid = false; this.password.msg = 'The password field must be at least 6 characters.'; this.formIsValid = false; }
            if (this.confirmPassword.val === '') { this.confirmPassword.isValid = false; this.confirmPassword.msg = 'Confirm New Password must not be empty.'; this.formIsValid = false; }
            else if (this.confirmPassword.val.length < 6) { this.confirmPassword.isValid = false; this.confirmPassword.msg = 'The confirm password field must be at least 6 characters.'; this.formIsValid = false; }
            else if (this.password.val !== this.confirmPassword.val) { this.confirmPassword.isValid = false; this.confirmPassword.msg = 'New Password and Confirm New Password does not match.'; this.formIsValid = false; }
        },
        async saveBtnclicked() {
            this.validateForm(); if (!this.formIsValid) return; this.isUpdating = true;
            const CHANGE_PASSWORD_MUTATION = gql`
                mutation ChangePassword($current_password: String!, $password: String!, $confirm_password: String!) {
                    changePassword(current_password: $current_password, password: $password, confirm_password: $confirm_password) {
                        error
                        error_code
                        message
                    }
                }
            `;
            var error_msg = ''; var success_msg = '';
            try {
                await this.$apollo.mutate({
                    mutation: CHANGE_PASSWORD_MUTATION,
                    variables: { current_password: this.currentPassword.val, password: this.password.val, confirm_password: this.confirmPassword.val },
                }).then((data) => { if (data.data.changePassword.error) { error_msg = data.data.changePassword.message; } else { success_msg = data.data.changePassword.message; } })
                    .catch((error) => { error_msg = error; });
            } catch (e) { error_msg = e.message || 'Please try again.'; }
            this.isUpdating = false;
            if (error_msg) { this.showAlertMessage('error', String(error_msg)); }
            else { this.clearForm(); this.showAlertMessage('success', success_msg || 'Password updated successfully.'); this.$emit('updated', 'change-password', success_msg || 'Password updated successfully.'); }
        },
        showAlertMessage(type, message) { this.alertType = type; this.alertMessage = message; this.showAlert = true; var self = this; window.clearTimeout(this._alertTimer); this._alertTimer = window.setTimeout(function () { self.showAlert = false; }, 4000); },
        dismissAlert() { this.showAlert = false; window.clearTimeout(this._alertTimer); },
        getPasswordStrength() { if (!this.password.val) return 0; var s = 0, p = this.password.val; if (p.length >= 6) s += 20; if (p.length >= 8) s += 20; if (p.length >= 12) s += 20; if (/[a-z]/.test(p)) s += 10; if (/[A-Z]/.test(p)) s += 10; if (/[0-9]/.test(p)) s += 10; if (/[^A-Za-z0-9]/.test(p)) s += 10; return Math.min(s, 100); },
        getPasswordStrengthClass() { var s = this.getPasswordStrength(); if (s < 30) return 'bg-red-500'; if (s < 60) return 'bg-amber-500'; if (s < 80) return 'bg-indigo-400'; return 'bg-indigo-700'; },
        getPasswordStrengthLabel() { var s = this.getPasswordStrength(); if (s < 30) return 'Weak'; if (s < 60) return 'Fair'; if (s < 80) return 'Good'; return 'Excellent'; },
        getStrengthTextClass() { var s = this.getPasswordStrength(); if (s < 30) return 'text-red-600'; if (s < 60) return 'text-amber-700'; if (s < 80) return 'text-indigo-600'; return 'text-indigo-900'; },
        clearForm() { this.currentPassword = { val: '', isValid: true, msg: '' }; this.password = { val: '', isValid: true, msg: '' }; this.confirmPassword = { val: '', isValid: true, msg: '' }; }
    }
}
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
    transition: all 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
    transform: translateY(6px);
}
</style>
